import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';

export const SPECIAL_CHAR_REGEX = /[\/<>:?%*|"]/;
export const EMAIL_REGEX = /^\s*(.+)@(.+?)\s*$/;
export const USER_REGEX = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*)$/;
export const IP_DOMAIN_REGEX = /^(\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])$/;
export const DOMAIN_REGEX = /^(([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z]{2,})$/;
export const ZIP_REGEX = /^[0-9]{5}(?:-[0-9]{4})?$/;
export const NAME_REGEX = /^[a-zA-Z0-9\-' ,.]+$/;
export const MAX_USERNAME_LEN = 64;
/**
 * Custom form validator for number/float inputs. This will determine if the value has the appropriate
 * amount of max decimal places.
 * @param {number} decimalPlace [decimalPlace=2]
 * @return {ValidatorFn}
 */
export function maxDecimalPlaceValidator(decimalPlace: number = 2): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const input = (control.value !== null)
      ? control.value.toString()
      : '';

    if (input === '' || input.indexOf('.') === -1) {
      return null;
    }

    const decimalPlaces = input.split('.');

    if (decimalPlaces.length > 2) {
      return {
        maxDecimal: {
          max: decimalPlace,
          actual: 'error'
        }
      };
    }

    return (decimalPlaces[1].length <= decimalPlace)
      ? null
      : {
        maxDecimal: {
          max: decimalPlace,
          actual: decimalPlaces[1].length
        }
      };
  };
}
/**
 * Validates the email address by sections. Sections: {users}@{domain}
 * By separating out the sections of an email, it makes it easier to validate rather than having to validate
 * the entire email as a whole.
 * @param {FormControl} control
 * @return {any}
 */
export function emailValidator(control: FormControl) {
  if (isEmpty(control.value)) {
    return null;
  }

  const emailMatcher = EMAIL_REGEX.exec(control.value);
  if (isEmpty(emailMatcher)) {
    return { invalidEmailAddress: true };
  }

  // here we will validate the user=
  if (emailMatcher && emailMatcher.length >= 2) {
    if (!isValidUser(emailMatcher[1])) {
      return { invalidEmailAddress: true };
    }

    // here we validate the domain
    if (!isValidDomain(emailMatcher[2])) {
      return { invalidEmailAddress: true };
    }
  }
  return null;

}

export function passwordValidator(control: FormControl): void {
  const password = control.get('password')?.value;
  const rePassword = control.get('rePassword')?.value;

  if (password !== rePassword) {
    control.get('rePassword')?.setErrors({ MatchPassword: true });
  }
}

export function zipCodeValidator(control: FormControl) {
  const value = control.value ? control.value.toString() : ''; // Converted to string for REGEX evaluation, just in case it is a number.
  const zipMatch = ZIP_REGEX.exec(value);

  if (isEmpty(zipMatch)) {
    return { invalidZip: true };
  }
  return null;

}

/**
 * Validator that checks for invalid emails
 *
 * @export
 * @returns {ValidatorFn}
 */
export function invalidEmailValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // Validator.required should handle empty strings
    if (control.value === '') {
      return null;
    }
    const data = EMAIL_REGEX.exec(control.value);
    return data === null || !isValidUser(data[1]) || !isValidDomain(data[2])
      ? { invalidEmail: { value: control.value } }
      : null;
  };
}

/**
 * Returns true if the domain component of an email address is valid.
 * @param {string} domain
 * @return {boolean}
 */
function isValidDomain(domain: string): boolean {
  // see if domain is an IP address in brackets
  if (domain === null) {
    return false;
  }
  return IP_DOMAIN_REGEX.test(domain) || DOMAIN_REGEX.test(domain);
}

/**
 * Returns true if the value passed contain special character/s or if value passed is blank
 * @param {string} value
 * @return {boolean}
 */
export function containsSpecialCharacter(value: string): boolean {
  if (value === null) {
    return true;
  }

  return SPECIAL_CHAR_REGEX.test(value);
}

/**
 * Returns true if the name passed is valid
 * @param {string} name
 * @return {boolean}
 */
export function isValidName(name: string): boolean {
  if (name === null) {
    return false;
  }

  return NAME_REGEX.test(name);
}

/**
 * Returns true if the user component of an email address is valid.
 * @param {string} user
 * @return {boolean}
 */
function isValidUser(user: string): boolean {
  if (user === null || user.length > MAX_USERNAME_LEN) {
    return false;
  }
  return USER_REGEX.test(user);
}

/**
 * Validate the number input by setting the max total.
 * @param {Number} max
 * @return {ValidatorFn}
 */
export function maxValue(max: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const input = control.value;

    if (input > max) {
      return { maxValue: { max } };
    }

    return null;
  };
}

/**
 * Validate the number input by restricting it to positive numbers only
 * @param {FormControl} control
 * @return {[key: string]: any}
 */
export function positiveNumber(control: FormControl): { [key: string]: any } | null {
  const input = Number(control.value);

  if (input < 0) {
    return { nonPositive: true };
  }

  return null;
}

/**
 * Validator that sets up input so that it can't match the given regular expression
 * @param {RegExp} nameRe
 * @return {ValidatorFn}
 */
export function excludedValue(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? { forbiddenName: { value: control.value } } : null;
  };
}

